import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import InfiniteScroll from 'react-infinite-scroll-component';
import { faAddressCard } from '@fortawesome/free-regular-svg-icons';
import { UseFormReset } from 'react-hook-form';
import ConfirmationModal from '../../../../../core/components/confirmation-modal/ConfirmationModal';
import { useAppSelector } from '../../../../../store/hooks';
import Button from '../../../../../core/components/button/Button';
import { PermissionEnum } from '../../../../../core/enums/dictionariesEnums';
import AddContactModal from '../../../../../common/components/contacts/AddContactModal';
import { infiniteScrollInitPaging, initDataListWithIds } from '../../../../../core/types/coreTypes';
import {
  setOrderContactsPaging,
  setOrderExistingContacts,
  setOrderExistingContactsPaging,
} from '../../../../../store/slices/ordersSlice';
import {
  orderContactsPagingSelector,
  orderContactsSelector,
  orderExistingContactsPagingSelector,
  orderExistingContactsSelector,
} from '../../../../../store/selectors/ordersSelector';
import { initAddressFields } from '../../../../../common/types/commonTypes';
import { createContactUtil } from '../../../../../common/utils/createContactUtils';
import {
  addOrderExistingContactsThunk,
  createNewOrderContactThunk,
  getOrderExistingContactsThunk,
  removeOrderContactThunk,
} from '../../../../../store/thunks/orders/orderContactsThunks';
import ContactLine from '../../../../../common/components/contacts/ContactLine';
import EmptyContentList from '../../../../../core/components/empty-content-list/EmptyContentList';
import { CustomerContactFiltersType } from '../../../../../customers/customer-view-page/types/customerVewPageTypes';
import AddressModal from '../../../../../common/components/address/AddressModal';
import { customerDefaultContactsOrderingSelector } from '../../../../../store/selectors/coreOrderingSelectors';
import { useAddContact } from '../../../../../common/components/contacts/useAddContact';
import { orderDetailsSelector } from '../../../../../store/selectors/sharedSelectors';
import Restricted from '../../../../../permissions-handling/Restricted';
import { NewContactFormFields } from '../../../../../common/types/createNewContactSchema';

type OrderContactsProps = {
  isCurrentStatusArchived: boolean,
}

const OrderContacts:React.FC<OrderContactsProps> = ({ isCurrentStatusArchived }) => {
  const orderContacts = useAppSelector(orderContactsSelector);
  const orderContactsPaging = useAppSelector(orderContactsPagingSelector);
  const orderDetails = useAppSelector(orderDetailsSelector);
  const orderExistingContacts = useAppSelector(orderExistingContactsSelector);
  const orderExistingContactsPaging = useAppSelector(orderExistingContactsPagingSelector);
  const defaultExistingContactsOrdering = useAppSelector(customerDefaultContactsOrderingSelector);

  const {
    id,
    dispatch,
    isRemove,
    setIsRemove,
    lineId,
    setLineId,
    addContact,
    setAddContact,
    isLocation,
    setLocation,
    addressFields,
    setAddressFields,
    mode,
    setMode,
  } = useAddContact();

  const loadMore = () => {
    const newValues = { ...orderContactsPaging, page: orderContactsPaging.page + 1 };
    dispatch(setOrderContactsPaging(newValues));
  };

  useEffect(() => {
    return () => {
      dispatch(setOrderContactsPaging(infiniteScrollInitPaging));
    };
    // eslint-disable-next-line
  }, [])

  const onCancel = () => {
    setMode('');
    dispatch(setOrderExistingContactsPaging({ ...orderExistingContactsPaging, page: 1 }));
    dispatch(setOrderExistingContacts(initDataListWithIds));
  };
  const closeModal = () => {
    setAddressFields(initAddressFields);
    setAddContact(false);
    onCancel();
  };

  const createNewContact = (values: NewContactFormFields, reset: UseFormReset<NewContactFormFields>) => {
    const { location, valuesCopy } = createContactUtil(addressFields, values);
    id && dispatch(createNewOrderContactThunk({
      orderId: +id,
      data: {
        ...location,
        ...valuesCopy,
      },
      closeModal: () => {
        setMode('');
        setAddressFields(initAddressFields);
        reset();
        setAddContact(false);
      },
    }));
  };

  const getExistingContacts = () => {
    orderDetails && defaultExistingContactsOrdering && dispatch(getOrderExistingContactsThunk({
      customerId: orderDetails.order.customerId,
      filters: { ...orderExistingContactsPaging, order: defaultExistingContactsOrdering },
      notAddedToOrderId: orderDetails.order.id,
    }));
  };

  const addSelectedContacts = (customerContactIds: number[]) => {
    orderDetails && dispatch(addOrderExistingContactsThunk({
      orderId: orderDetails.order.id,
      customerId: orderDetails.order.customerId,
      customerContactIds,
      closeModal,
    }));
  };

  const setExistedContactsPaging = (filters: CustomerContactFiltersType) => {
    dispatch(setOrderExistingContactsPaging(filters));
  };

  return (
    <>
      <ConfirmationModal
        isVisible={isRemove}
        onCancel={() => {
          setIsRemove(false);
          setLineId(undefined);
        }}
        isDelete={false}
        confirmHandler={() => id && lineId && dispatch(removeOrderContactThunk({
          orderId: +id,
          customerContactId: lineId,
          closeModal: () => {
            setIsRemove(false);
            setLineId(undefined);
          },
        }))}
      />
      <AddContactModal
        isVisible={addContact}
        onCancel={onCancel}
        closeModal={closeModal}
        addressFields={addressFields}
        setLocation={setLocation}
        setAddressFields={setAddressFields}
        companyNameValue={orderDetails?.order.customerName || ''}
        createNewContact={createNewContact}
        mode={mode}
        setMode={setMode}
        existedContacts={orderExistingContacts}
        getExistingContacts={getExistingContacts}
        existingPaging={orderExistingContactsPaging}
        addSelectedContacts={addSelectedContacts}
        setExistedContactsPaging={setExistedContactsPaging}
      />
      <AddressModal
        isVisible={isLocation}
        onCancel={() => {
          setLocation(false);
        }}
        addressFields={addressFields}
        onSubmit={(values) => {
          setAddressFields(values);
          setLocation(false);
        }}
        isExtendedFields={false}
      />
      <section className="contacts">
        <Restricted to={PermissionEnum.OrderEditFields}>
          <Button
            label="Add contact"
            icon={<FontAwesomeIcon icon={faCirclePlus} />}
            htmlType="button"
            onClick={() => setAddContact(true)}
            designType="dark"
            disabled={isCurrentStatusArchived}
            reversed
          />
        </Restricted>
      </section>
      <div className="contacts__wrapper">
        {orderContacts.items?.length > 0
          ? <InfiniteScroll
            dataLength={orderContacts.items.length}
            next={loadMore}
            hasMore={orderContactsPaging.page < orderContacts.pages}
            loader={<span className="contacts__loader">Loading...</span>}
          >
            <ul className="lines-list">
              {orderContacts.items.map((line) => (
                <li
                  className="lines-list__item"
                  key={line.id}
                >
                  <ContactLine
                    setIsRemove={(id) => {
                      setIsRemove(true);
                      setLineId(id);
                    }}
                    line={line}
                    deleteContactPermission={PermissionEnum.OrderEditFields}
                    disabled={isCurrentStatusArchived}
                  />
                </li>))}
            </ul>
          </InfiniteScroll>

          : <EmptyContentList icon={faAddressCard} text="Please add a contact to this order" />}
      </div>
    </>
  );
};

export default OrderContacts;
